/**
 * notchesScorecardService — fetches the public dual-model scorecard payload.
 * Backed by GET /notches-scorecard on bb-api HTTP API GW. Public, no auth.
 * Cached at the Lambda for 10 minutes.
 */

const API_BASE = 'https://icq67h6a7f.execute-api.us-east-2.amazonaws.com';

export type ScorecardOutcome = 'hit' | 'miss' | 'open' | 'neutral';
export type ScorecardModel = 'COT' | 'GEX' | 'IV' | 'AVWAP';

export interface ScorecardAggregate {
  symbol: string;
  tradable: string;
  label: string;
  n: number;
  openN: number;
  hitPct: number | null;
}

export interface CotRow {
  date: string;
  symbol: 'ES' | 'NQ' | 'GC';
  tradable: 'SPY' | 'QQQ' | 'GLD';
  tag: string;
  reading: string;
  regime: 'stretched' | 'extreme' | 'neutral';
  outcome: ScorecardOutcome;
  deltaPct: number | null;
}

export interface GexRow {
  date: string;
  symbol: 'SPX' | 'SPY' | 'QQQ' | 'IWM';
  tradable: 'SPY' | 'QQQ' | 'IWM';
  tag: string;
  reading: string;
  regime: 'expand-expected' | 'compress-expected' | 'transition';
  outcome: ScorecardOutcome;
  ratio: number | null;
}

export interface CotBlock {
  aggregates: ScorecardAggregate[];
  rows: CotRow[];
  meta: { horizonDays: number };
}

export interface GexBlock {
  aggregates: ScorecardAggregate[];
  rows: GexRow[];
  meta: {
    lookbackDays: number;
    forwardDays: number;
    expandMult: number;
    compressMult: number;
  };
}

export interface IvRow {
  date: string;
  symbol: string;
  tag: 'IVR_HIGH' | 'IVR_LOW';
  reading: string;
  regime: 'high' | 'low';
  outcome: ScorecardOutcome;
  ivrAtCross: number;
  daysToHit: number | null;
  hitOnIvr: number | null;
}

export interface IvBlock {
  aggregates: ScorecardAggregate[];
  rows: IvRow[];
  meta: {
    highBand: number;
    lowBand: number;
    revertLevel: number;
    windowDays: number;
  };
}

export interface AvwapRow {
  date: string;
  symbol: string;
  tag: 'AVWAP_HELD' | 'AVWAP_BROKE' | 'AVWAP_TEST';
  reading: string;
  regime: 'test';
  outcome: ScorecardOutcome;
  spot: number;
  avwap: number;
  anchorDate: string;
  exitClose: number | null;
  exitDistPct: number | null;
}

export interface AvwapBlock {
  aggregates: ScorecardAggregate[];
  rows: AvwapRow[];
  meta: {
    anchorLookback: number;
    priorWindow: number;
    priorAwayPct: number;
    forwardDays: number;
    breakPct: number;
  };
}

export interface ScorecardPayload {
  cot: CotBlock;
  gex: GexBlock;
  iv: IvBlock;
  avwap: AvwapBlock;
  lookbackDays: number;
  fetchedAt: string;
  cached?: boolean;
}

export async function fetchNotchesScorecard(): Promise<ScorecardPayload> {
  const res = await fetch(`${API_BASE}/notches-scorecard`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`scorecard fetch failed: HTTP ${res.status}`);
  return res.json();
}

// Unified row shape for the reading log.
export interface UnifiedRow {
  model: ScorecardModel;
  date: string;
  symbol: string;
  tradable: string;
  tag: string;                        // raw model tag, used for trade-implication lookup
  reading: string;
  outcome: ScorecardOutcome;
  displayValue: string;               // deltaPct or ratio, pre-formatted
  displayColor: 'pos' | 'neg' | 'neutral';
  regime: string;
}

export function unifyRows(cot: CotRow[], gex: GexRow[], iv: IvRow[], avwap: AvwapRow[]): UnifiedRow[] {
  const c: UnifiedRow[] = cot.map((r) => ({
    model: 'COT',
    date: r.date, symbol: r.symbol, tradable: r.tradable, tag: r.tag,
    reading: r.reading,
    outcome: r.outcome,
    displayValue: r.deltaPct == null ? '—' : `${r.deltaPct > 0 ? '+' : ''}${r.deltaPct.toFixed(2)}%`,
    displayColor: r.deltaPct == null ? 'neutral' : r.deltaPct < 0 ? 'neg' : 'pos',
    regime: r.regime,
  }));
  const g: UnifiedRow[] = gex.map((r) => ({
    model: 'GEX',
    date: r.date, symbol: r.symbol, tradable: r.tradable, tag: r.tag,
    reading: r.reading,
    outcome: r.outcome,
    displayValue: r.ratio == null ? '—' : `${r.ratio.toFixed(2)}× vol`,
    displayColor: r.ratio == null ? 'neutral'
      : (r.ratio >= 1.15 || r.ratio <= 0.85) ? 'pos' : 'neutral',
    regime: r.regime,
  }));
  const i: UnifiedRow[] = iv.map((r) => ({
    model: 'IV',
    date: r.date, symbol: r.symbol, tradable: r.symbol, tag: r.tag,
    reading: r.reading,
    outcome: r.outcome,
    displayValue: r.hitOnIvr != null && r.daysToHit != null
      ? `${r.ivrAtCross.toFixed(0)} → ${r.hitOnIvr.toFixed(0)} · ${r.daysToHit}d`
      : r.outcome === 'miss' ? `${r.ivrAtCross.toFixed(0)} (no revert)`
      : `${r.ivrAtCross.toFixed(0)} at cross`,
    displayColor: r.outcome === 'hit' ? 'pos' : r.outcome === 'miss' ? 'neg' : 'neutral',
    regime: r.regime,
  }));
  const a: UnifiedRow[] = avwap.map((r) => ({
    model: 'AVWAP',
    date: r.date, symbol: r.symbol, tradable: r.symbol, tag: r.tag,
    reading: r.reading,
    outcome: r.outcome,
    displayValue: r.exitDistPct != null
      ? `${r.exitDistPct > 0 ? '+' : ''}${r.exitDistPct.toFixed(2)}% at day 5`
      : r.outcome === 'open' ? 'in flight' : '—',
    displayColor: r.outcome === 'hit' ? 'pos' : r.outcome === 'miss' ? 'neg' : 'neutral',
    regime: r.regime,
  }));
  return [...c, ...g, ...i, ...a].sort((x, y) => y.date.localeCompare(x.date));
}
